/**
 * This is the COMMUNITY STANDARD PAGE
 *
 * __NOTE__: Community Standard pages do NOT have a unique slug, so when retrieving a Community Standard page we MUST use the contentful sys.id
 * - Pages can have multiple parents but all will be of the same content-type COMMUNITY STANDARD type
 */

import React from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';
import { apolloClient, setApolloHeaders } from '@src/services/apollo';
import {
  NationalBasePageQuery,
  CommunityBasePageQuery,
  PageCommunityStandardPart1ContentBlocksQuery,
  PageCommunityStandardPart2ContentBlocksQuery,
  PageCommunityStandardPart3ContentBlocksQuery,
  PageCommunityStandardPart4ContentBlocksQuery,
  PageCommunityStandardPathsQuery,
  PageCommunityStandardQuery,
  PageCommunityStandardQueryVariables,
  GlobalSettingsQuery,
  PageCommunityPropertiesQuery,
  PageCommunityPropertiesQueryVariables,
  PageCommunityStandardPathQuery
} from '@src/graphql/gql-types';
import PageCommunityStandardTemplate from '@src/templates/PageCommunityStandard';
import {
  pageCommunityStandardQuery,
  pageCommunityStandardPathsQuery,
  globalSettingsQuery,
  pageCommunityStandardPart1ContentBlocksQuery,
  pageCommunityStandardPart2ContentBlocksQuery,
  pageCommunityStandardPart3ContentBlocksQuery,
  pageCommunityStandardPart4ContentBlocksQuery,
  communityBasePageQuery,
  nationalBasePageQuery,
  pageCommunityPropertiesQuery,
  pageCommunityStandardPathQuery
} from '@src/graphql/queries';
import { flattenPagePaths, getSlugsFromArray } from '@src/utils/url';
import { CommunityStandardPageProps } from '@src/types/page';
import {
  getCommunityDetailSegmentFromFlattenPagePaths,
  mergeContentBlocks
} from '@src/utils/helpers';
import { getValidPathsOnly } from '@src/utils/build/paths';
import { hashObjectToString } from '@src/utils/build/hash';
import { base64EncodeHero } from '@src/utils/build/base64EncodeHero';

const CommunityStandardPage: React.FC<CommunityStandardPageProps> = (props) => (
  <PageCommunityStandardTemplate {...props} />
);

export default CommunityStandardPage;

/**
 * Create the paths params and cache the needed data to be passed to GetStaticProps
 * @param pages
 * @returns
 */
const createPathParams = (pages: any[]): any[] => {
  return pages
    .filter((page) => !!page?.slug)
    .map((page) => {
      /**
       * Get an array of the page paths (This flattens the nested parent path into a usable array) */
      const arrayOfPages = flattenPagePaths(page);
      /**
       * Now extract the slug names in to an array : ['page-a','page-b','page-c']
       * - This is used for the nextJS catch-all-routes dynamic path, which requires an array */
      const slugCommunityStandardCatchAllArray =
        getSlugsFromArray(arrayOfPages);

      /**
       * Using __shift()__ we get the first slug in the array which is the "community" slug.
       * - __shift()__ also modifies __slugCommunityStandardCatchAllArray__ leaving just the needed slugs for the community standard catch-all-routes */
      const communitySlug = slugCommunityStandardCatchAllArray.shift();

      /**
       * __Params to use for hash (Must match the same params passed in nextJS (in paths below))__ */
      const path = {
        params: {
          slug_community_standard: slugCommunityStandardCatchAllArray,
          slug_community_detail: communitySlug ?? ''
        }
      };

      return path;
    });
};

/**
 * Build static paths
 * - Based on the GraphQL query `pageCommunityStandardPaths.query.ts` this should return upto __4 levels__ of __Community Standard__ pages, as well as the __Community parent__.
 * - Please confirm with checking in the GraphQL query for what is returned.
 * - This uses nextJS [catch-all-routes](https://nextjs.org/docs/routing/dynamic-routes#catch-all-routes)
 *
 *  __NOTE__: Community Standard pages do NOT have a unique slug, so when retrieving a Community Standard page we MUST use the contentful sys.id
 *  - Hence we store the page sys.id in a cache file for retrieval in getStaticProps
 * @returns path params
 */
export const getStaticPaths: GetStaticPaths = async () => {
  const pageQueryResponse =
    await apolloClient.query<PageCommunityStandardPathsQuery>({
      query: pageCommunityStandardPathsQuery,
      variables: { preview: false }
    });

  if (pageQueryResponse?.errors) {
    console.error(pageQueryResponse.errors);
  }

  const pages =
    pageQueryResponse.data?.pageCommunityStandardCollection?.items || [];

  const paths = createPathParams(pages);
  const validPaths = getValidPathsOnly(paths);

  return {
    paths: validPaths,
    fallback: false
  };
};

export const getStaticProps: GetStaticProps = async ({ preview, params }) => {
  /**
   * NOTE:
   * Hash the params to get the lookup ID to find the correct page. This is a server side function only.
   * This is created and applied to the page ('pageCommunityStandard' content-type) through the webhook 'entryAssignTagToCommunity'
   *  __IF THE HASH IS NOT CREATED AND APPLIED THEN THE PAGE WILL NOT BE FOUND__
   */
  const nextJsParamsHashId = params ? hashObjectToString(params) : 'no-hash';

  // START: === Use the params hash to lookup/get the correct page
  if (preview) {
    setApolloHeaders(process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW_TOKEN);
  }

  const pageMetaData = await apolloClient.query<PageCommunityStandardPathQuery>(
    {
      query: pageCommunityStandardPathQuery,
      variables: { nextJsParamsHashId: nextJsParamsHashId, preview: preview }
    }
  );

  if (pageMetaData?.errors) {
    console.error(pageMetaData.errors);
  }

  // We need the sys.id, navigation_id and community_id for the queries below
  const pageExtraData = pageMetaData.data?.pagePath?.items?.[0];

  /**
   * Get an array of the page paths (This flattens the nested parent path into a usable array) */
  const arrayOfPagesPaths = flattenPagePaths(pageExtraData);
  // Get Community Details Segment , which will have reference to the Navigation to use
  const communityDetailSegments =
    getCommunityDetailSegmentFromFlattenPagePaths(arrayOfPagesPaths);

  const lookupData = {
    id: pageExtraData?.sys.id ?? '',
    navigation_id: communityDetailSegments?.navigation?.sys?.id || '',
    community_id: communityDetailSegments?.sys?.id || ''
  };
  // END ===
  // Get all the data required for the page
  const [
    globalSettingsQueryResponse,
    nationalBasePageQueryResponse,
    communityBasePageQueryResponse,
    pageQueryResponse,
    pageCommunityStandardPart1ContentBlocksQueryResponse,
    pageCommunityStandardPart2ContentBlocksQueryResponse,
    pageCommunityStandardPart3ContentBlocksQueryResponse,
    pageCommunityStandardPart4ContentBlocksQueryResponse,
    pageCommunityPropertiesResponse
  ] = await Promise.all([
    apolloClient.query<GlobalSettingsQuery>({
      query: globalSettingsQuery,
      variables: {
        preview: preview
      }
    }),

    apolloClient.query<NationalBasePageQuery>({
      query: nationalBasePageQuery,
      variables: {
        preview: preview
      }
    }),

    apolloClient.query<CommunityBasePageQuery>({
      query: communityBasePageQuery,
      variables: {
        id: lookupData?.navigation_id,
        community_id: lookupData?.community_id,
        preview: preview
      }
    }),

    apolloClient.query<PageCommunityStandardQuery>({
      query: pageCommunityStandardQuery,
      variables: { id: lookupData?.id, preview: preview }
    }),

    apolloClient.query<
      PageCommunityStandardPart1ContentBlocksQuery,
      PageCommunityStandardQueryVariables
    >({
      query: pageCommunityStandardPart1ContentBlocksQuery,
      variables: { id: lookupData?.id, preview: preview }
    }),
    apolloClient.query<
      PageCommunityStandardPart2ContentBlocksQuery,
      PageCommunityStandardQueryVariables
    >({
      query: pageCommunityStandardPart2ContentBlocksQuery,
      variables: { id: lookupData?.id, preview: preview }
    }),
    apolloClient.query<
      PageCommunityStandardPart3ContentBlocksQuery,
      PageCommunityStandardQueryVariables
    >({
      query: pageCommunityStandardPart3ContentBlocksQuery,
      variables: { id: lookupData?.id, preview: preview }
    }),
    apolloClient.query<
      PageCommunityStandardPart4ContentBlocksQuery,
      PageCommunityStandardQueryVariables
    >({
      query: pageCommunityStandardPart4ContentBlocksQuery,
      variables: { id: lookupData?.id, preview: preview }
    }),
    apolloClient.query<
      PageCommunityPropertiesQuery,
      PageCommunityPropertiesQueryVariables
    >({
      query: pageCommunityPropertiesQuery,
      variables: {
        communitySlug: params?.slug_community_detail as string,
        preview: preview,
        skip: 0,
        limit: 8
      }
    })
  ]);

  if (globalSettingsQueryResponse?.errors) {
    console.error(pageQueryResponse.errors);
  }

  if (nationalBasePageQueryResponse?.errors) {
    console.error(nationalBasePageQueryResponse.errors);
  }

  if (communityBasePageQueryResponse?.errors) {
    console.error(communityBasePageQueryResponse.errors);
  }

  if (pageQueryResponse?.errors) {
    console.error(pageQueryResponse.errors);
  }
  if (pageCommunityStandardPart1ContentBlocksQueryResponse?.errors) {
    console.error(pageCommunityStandardPart1ContentBlocksQueryResponse.errors);
  }
  if (pageCommunityStandardPart2ContentBlocksQueryResponse?.errors) {
    console.error(pageCommunityStandardPart2ContentBlocksQueryResponse.errors);
  }
  if (pageCommunityStandardPart3ContentBlocksQueryResponse?.errors) {
    console.error(pageCommunityStandardPart3ContentBlocksQueryResponse.errors);
  }
  if (pageCommunityStandardPart4ContentBlocksQueryResponse?.errors) {
    console.error(pageCommunityStandardPart4ContentBlocksQueryResponse.errors);
  }
  if (pageCommunityPropertiesResponse?.errors) {
    console.error(pageCommunityPropertiesResponse.errors);
  }

  const items = mergeContentBlocks([
    pageCommunityStandardPart1ContentBlocksQueryResponse?.data,
    pageCommunityStandardPart2ContentBlocksQueryResponse?.data,
    pageCommunityStandardPart3ContentBlocksQueryResponse?.data,
    pageCommunityStandardPart4ContentBlocksQueryResponse?.data
  ]);

  const pagePayload = await base64EncodeHero(pageQueryResponse?.data);

  const templatePropsData: CommunityStandardPageProps = {
    globalSettingsPayload: globalSettingsQueryResponse?.data,
    nationalBasePagePayload: nationalBasePageQueryResponse?.data,
    communityBasePagePayload: communityBasePageQueryResponse?.data,
    pageContentBlocks: items,
    pageCommunityPropertiesPayload: pageCommunityPropertiesResponse?.data,
    pagePayload: pagePayload as PageCommunityStandardQuery
  };

  return {
    props: {
      ...templatePropsData
    }
  };
};
